/**
 * Agent Registry
 *
 * Central registry of all available agents. New agents are added here
 * and automatically appear in the agent selector UI.
 */

import { lazy } from 'react';
import type { AgentDefinition } from './types';

/**
 * All registered agents
 */
export const AGENTS: AgentDefinition[] = [
  {
    // Alerts lives under Agents as a registry entry (it grew out of the retired
    // Echo agent). The page itself still lives in pages/alerts — only the route
    // and nav surface moved, so a future re-org is a registry edit, not a code move.
    id: 'alerts',
    slug: 'alerts',
    name: 'Alerts Agent',
    navLabel: 'Alerts',
    description: 'Real-time alert fires, stats, and management for radio traffic',
    icon: 'HiExclamationCircle',
    component: lazy(() => import('../pages/alerts').then((m) => ({ default: m.AlertsPage }))),
    enabled: true,
    alwaysEntitled: true
  },
  {
    id: 'ksfo-delay',
    slug: 'ksfo-delay',
    name: 'Taxi-in Delay Agent: SFO Airport',
    navLabel: 'SFO Delay',
    description: 'Analyze taxi delays at San Francisco International Airport',
    icon: 'HiClock',
    component: lazy(() => import('./ksfo-delay')),
    enabled: true,
    entityType: 'flight'
  },
  {
    id: 'summary-agent',
    slug: 'summary-agent',
    name: 'Daily Operations Agent: Oakland Airport',
    navLabel: 'Daily Ops',
    description: 'Daily activity and incidents at the airport',
    icon: 'HiBell',
    component: lazy(() => import('./summary-agent')),
    enabled: true,
    entityType: 'incident'
  },
  {
    id: 'activity-ledger',
    slug: 'activity-ledger',
    name: 'Activity Ledger',
    navLabel: 'Ledger',
    description: 'Real-time activity classification from radio transmissions',
    icon: 'HiInboxStack',
    component: lazy(() => import('./activity-ledger')),
    enabled: true,
    entityType: 'activity'
  },
  {
    id: 'situational-awareness',
    slug: 'situational-awareness',
    name: 'Situational Awareness: Indianapolis',
    navLabel: 'Awareness',
    description: 'Correlate radio communications with IoT and drone telemetry',
    icon: 'HiEye',
    component: lazy(() => import('./situational-awareness')),
    enabled: true,
    entityType: 'telemetry'
  }
  // Future agents can be added here:
  // {
  //   id: 'atc-summary',
  //   slug: 'atc-summary',
  //   name: 'ATC Communication Summary',
  //   description: 'Summarize and analyze ATC communications',
  //   icon: 'HiSignal',
  //   component: lazy(() => import('./atc-summary')),
  //   enabled: false,
  //   entityType: 'communication',
  // },
];

/**
 * Get an agent by its slug
 * Returns undefined if not found or disabled
 */
export function getAgentBySlug(slug: string): AgentDefinition | undefined {
  return AGENTS.find((a) => a.slug === slug && a.enabled !== false);
}

/**
 * Get all enabled agents
 */
export function getEnabledAgents(): AgentDefinition[] {
  return AGENTS.filter((a) => a.enabled !== false);
}

/**
 * Check if an agent exists and is enabled
 */
export function isAgentEnabled(slug: string): boolean {
  return getAgentBySlug(slug) !== undefined;
}
